import React from 'react';
import { Check, Wrench, Phone, MapPin, Shield, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const Servicing = () => {
  const services = [
    {
      name: "Full Service (Trailer & Habitation)",
      type: "Caravan (Single Axle)",
      price: "£235",
      description: "Complete service covering both trailer and habitation systems"
    },
    {
      name: "Full Service (Trailer & Habitation)",
      type: "Caravan (Twin Axle)",
      price: "£265",
      description: "Comprehensive service for twin axle caravans"
    },
    {
      name: "Habitation Only",
      type: "Motorhome/Caravan",
      price: "£195",
      description: "Habitation area service for motorhomes and caravans"
    },
    {
      name: "Call Out Charge",
      type: "First 30 Minutes Included",
      price: "£65",
      description: "Standard call out charge with 30 minutes included"
    },
    {
      name: "Hourly Rate",
      type: "Additional Work",
      price: "£50",
      description: "Hourly rate for additional work beyond the call out"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="Mobile Motorhome Habitation Servicing Suffolk & Essex" rawTitle url="/servicing"
        structuredData={generateBreadcrumbSchema([{ name: 'Mobile Servicing', path: '/servicing' }])}
        description="Mobile motorhome and caravan habitation servicing across Suffolk and Essex - gas safety, damp checks, electrics and appliances at your home or storage site. Book with Millie's Motorhome Hire." />
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Badge className="mb-4 bg-yellow-500 text-slate-900 hover:bg-yellow-600 text-lg px-6 py-2">
            WE COME TO YOU!
          </Badge>
          <h1 className="text-5xl font-bold mb-6">Habitation Checks & Mobile Servicing</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            Professional mobile servicing for caravans and motorhomes. ACOPS Qualified Engineer & MCEA Approved - Fully Insured
          </p>
          <img 
            src="https://customer-assets.emergentagent.com/job_better-website-5/artifacts/zx8yguvf_Screenshot%202026-01-11%20101450.png"
            alt="Habitation Checks Service"
            className="max-w-4xl mx-auto rounded-lg shadow-2xl"
          />
        </div>
      </section>

      {/* Certifications */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-6 text-center">
                <div className="bg-green-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-xl mb-2">ACOPS Qualified Engineer</h3>
                <p className="text-slate-600">Professionally certified and trained</p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8" />
                </div>
                <h3 className="font-bold text-xl mb-2">MCEA Approved</h3>
                <p className="text-slate-600">Fully insured for your peace of mind</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services & Pricing */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Services & Pricing</h2>
            <p className="text-xl text-slate-600">Professional mobile servicing at competitive rates</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {services.map((service, idx) => (
              <Card key={idx} className="hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <Wrench className="h-8 w-8 text-blue-600" />
                    <span className="text-3xl font-bold text-blue-600">{service.price}</span>
                  </div>
                  <h3 className="font-bold text-xl mb-2">{service.name}</h3>
                  <p className="text-slate-600 font-semibold mb-2">{service.type}</p>
                  <p className="text-slate-600 text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-8 text-center">
              <p className="text-lg font-semibold text-slate-900 mb-2">
                All prices are inclusive of VAT
              </p>
              <p className="text-slate-600">
                Any additional parts charged separately
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What's Included in Our Habitation Service</h2>
            <p className="text-xl text-slate-600">Comprehensive inspection covering all essential systems</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {/* Gas System */}
            <Card className="border-orange-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-orange-100 rounded-full p-2">
                    <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg text-slate-900">Gas System</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Gas pressure test & tightness test</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Regulator inspection & operation</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Gas pipework inspection</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>All gas appliance checks</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Flame failure device testing</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Gas drop-out hole inspection</span></li>
                </ul>
              </CardContent>
            </Card>

            {/* Electrical System */}
            <Card className="border-yellow-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-yellow-100 rounded-full p-2">
                    <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg text-slate-900">Electrical System</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>240V mains system check</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>12V DC system check</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>RCD & MCB operation test</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Battery condition & connections</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Charger operation check</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>All interior & exterior lights</span></li>
                </ul>
              </CardContent>
            </Card>

            {/* Water System */}
            <Card className="border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 rounded-full p-2">
                    <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg text-slate-900">Water System</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Fresh water tank inspection</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Water pump operation check</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>All taps & connections</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Water heater operation</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Waste water system</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Toilet cassette & seals</span></li>
                </ul>
              </CardContent>
            </Card>

            {/* Heating & Ventilation */}
            <Card className="border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-red-100 rounded-full p-2">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg text-slate-900">Heating & Ventilation</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Space heater operation</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Blown air system check</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Flue & combustion check</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>All fixed ventilation</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Roof lights & vents</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Window operation & seals</span></li>
                </ul>
              </CardContent>
            </Card>

            {/* Safety Checks */}
            <Card className="border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-100 rounded-full p-2">
                    <Shield className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="font-bold text-lg text-slate-900">Safety Checks</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Smoke alarm test</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Carbon monoxide alarm test</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Fire extinguisher check</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Emergency exit operation</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Door locks & catches</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Step operation & security</span></li>
                </ul>
              </CardContent>
            </Card>

            {/* Body & Damp */}
            <Card className="border-purple-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-purple-100 rounded-full p-2">
                    <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg text-slate-900">Body & Damp Check</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Full damp test with meter</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>All seals & sealant inspection</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Body panel inspection</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Roof condition check</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Floor condition check</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Awning rail & fittings</span></li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Service Report & Recommendations */}
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-600 text-white rounded-full p-2">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-xl text-slate-900">Comprehensive Service Report</h3>
                </div>
                <p className="text-slate-700 mb-4">
                  Every habitation service includes a detailed written report covering all checks performed, 
                  current condition of systems, and any areas of concern.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Full written report provided</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Gas safety certificate issued</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Damp readings recorded</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" /><span>Service sticker applied</span></li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-600 text-white rounded-full p-2">
                    <Wrench className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-xl text-slate-900">Recommendations & Follow-Up Repairs</h3>
                </div>
                <p className="text-slate-700 mb-4">
                  If we identify any issues during your service, we'll provide clear recommendations 
                  and can carry out follow-up repairs at your convenience.
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Priority items clearly highlighted</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Repair quotes provided</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Mobile repairs available</span></li>
                  <li className="flex items-start gap-2"><Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" /><span>Parts sourced & fitted</span></li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Choose Our Mobile Service?</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">We Come To You</h3>
                <p className="text-slate-600 text-sm">
                  No need to transport your vehicle - we bring the service to your location
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Fully Qualified</h3>
                <p className="text-slate-600 text-sm">
                  ACOPS qualified engineer with MCEA approval and full insurance
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Convenient Timing</h3>
                <p className="text-slate-600 text-sm">
                  Flexible scheduling to suit your needs - weekdays and weekends available
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Wrench className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Comprehensive Service</h3>
                <p className="text-slate-600 text-sm">
                  Complete inspection with detailed report and certification
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Products & Partnerships */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Products & Equipment</h2>
            <p className="text-xl text-slate-600">Official dealer and authorised reseller</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-blue-200 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <svg className="h-10 w-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Gaslow Dealer</h3>
                <p className="text-slate-700 mb-4">
                  We are an official dealer for Gaslow refillable LPG systems - the safe, convenient, and cost-effective solution for your motorhome gas needs.
                </p>
                <ul className="text-left space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>Refillable LPG systems installation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>System servicing and maintenance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>Expert advice and support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-green-200 hover:shadow-xl transition-all">
              <CardContent className="p-8 text-center">
                <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Avtex Authorised Reseller</h3>
                <p className="text-slate-700 mb-4">
                  We are an authorised reseller of Avtex motorhome equipment - premium entertainment and navigation solutions designed specifically for motorhomes.
                </p>
                <ul className="text-left space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Avtex TVs and satellite systems</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Professional installation available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Full product range and warranty</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-600 mb-4">
              Interested in our products? Contact us for pricing and availability
            </p>
            <div className="flex gap-4 justify-center">
              <Button asChild>
                <a href="mailto:milliesmotorhomehire@gmail.com">Email for Quote</a>
              </Button>
              <Button variant="outline" asChild>
                <a href="https://wa.me/447527006966" target="_blank" rel="noopener noreferrer">
                  WhatsApp Us
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Book Your Service Today</h2>
          <p className="text-xl mb-8 text-blue-100">
            Get your caravan or motorhome serviced by qualified professionals at your location
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100" asChild>
              <a href="tel:07527006966">
                <Phone className="mr-2 h-5 w-5" />
                Call Now: 07527 006966
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600" asChild>
              <a href="mailto:milliesmotorhomehire@gmail.com">
                Email for Quote
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Servicing;
